// ═══════════════════════════════════════════════════════
//  CONSULTEC — Generador de Reportes PDF
//  src/services/pdfGenerator.js
//  Genera PDFs automáticos con jsPDF
// ═══════════════════════════════════════════════════════

// jsPDF se carga dinámicamente para no bloquear el bundle
let jsPDF = null
let autoTable = null

async function cargarJsPDF () {
  if (jsPDF) return
  try {
    const mod = await import('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js')
    jsPDF = mod.jsPDF || mod.default?.jsPDF
    await import('https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.1/dist/jspdf.plugin.autotable.min.js')
    console.log('✅ jsPDF cargado')
  } catch (err) {
    console.error('❌ Error cargando jsPDF:', err)
  }
}

// ── COLORES CONSULTEC ────────────────────────────────────
const COLORS = {
  navy:    [7, 24, 58],
  orange:  [240, 122, 35],
  blue:    [26, 95, 180],
  green:   [29, 158, 117],
  red:     [226, 75, 74],
  amber:   [239, 159, 39],
  gray:    [90, 106, 138],
  light:   [238, 241, 248],
  white:   [255, 255, 255]
}

// ── HEADER ESTÁNDAR ──────────────────────────────────────
function addHeader (doc, titulo, subtitulo, proyecto) {
  // Fondo navy
  doc.setFillColor(...COLORS.navy)
  doc.rect(0, 0, 210, 35, 'F')

  // Logo cuadrado
  doc.setFillColor(...COLORS.orange)
  doc.rect(14, 8, 10, 10, 'F')
  doc.setFillColor(...COLORS.blue)
  doc.rect(25, 8, 10, 10, 'F')
  doc.setFillColor(255, 255, 255, 0.1)
  doc.rect(14, 19, 10, 10, 'F')
  doc.setFillColor(255, 255, 255)
  doc.rect(18, 12, 13, 13, 'F')
  doc.setFillColor(...COLORS.navy)
  doc.rect(21, 15, 1.5, 7, 'F')
  doc.rect(18.5, 17, 7, 1.5, 'F')

  // Texto
  doc.setTextColor(...COLORS.white)
  doc.setFontSize(14)
  doc.setFont('helvetica', 'bold')
  doc.text('CONSULTEC', 40, 16)

  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(200, 210, 230)
  doc.text('FIELD MANAGER · CONSULTORÍA E INGENIERÍA', 40, 21)

  // Título del reporte
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.white)
  doc.text(titulo, 105, 14, { align: 'center' })

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(200, 210, 230)
  doc.text(subtitulo || '', 105, 20, { align: 'center' })

  // Proyecto
  if (proyecto) {
    doc.setFontSize(8)
    doc.setTextColor(...COLORS.white)
    doc.text(`Proyecto: ${proyecto}`, 105, 28, { align: 'center' })
  }

  // Línea naranja
  doc.setFillColor(...COLORS.orange)
  doc.rect(0, 35, 210, 1.5, 'F')

  return 44
}

// ── FOOTER ESTÁNDAR ──────────────────────────────────────
function addFooter (doc, pageNum, totalPages) {
  const y = 287
  doc.setFillColor(...COLORS.light)
  doc.rect(0, y - 2, 210, 12, 'F')
  doc.setFillColor(...COLORS.navy)
  doc.rect(0, y - 2, 4, 12, 'F')
  doc.setFontSize(7)
  doc.setTextColor(...COLORS.gray)
  doc.setFont('helvetica', 'normal')
  doc.text('CONSULTEC SA de CV · Field Manager · Documento generado automáticamente', 14, y + 4)
  doc.text(`Página ${pageNum} de ${totalPages}  ·  ${new Date().toLocaleString('es-MX')}`, 196, y + 4, { align: 'right' })
}

// ── KPI CARD ─────────────────────────────────────────────
function addKpiCard (doc, x, y, w, h, valor, label, color = COLORS.navy) {
  doc.setFillColor(...COLORS.light)
  doc.roundedRect(x, y, w, h, 2, 2, 'F')
  doc.setFillColor(...color)
  doc.rect(x, y, 3, h, 'F')
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...color)
  doc.text(String(valor), x + w / 2, y + h / 2 + 2, { align: 'center' })
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...COLORS.gray)
  doc.text(label, x + w / 2, y + h - 3, { align: 'center' })
}

// ── BARRA DE PROGRESO ────────────────────────────────────
function addProgressBar (doc, x, y, w, pct, color = COLORS.blue, label = '') {
  // Track
  doc.setFillColor(220, 226, 238)
  doc.roundedRect(x, y, w, 4, 1, 1, 'F')
  // Fill
  const fillW = Math.max(2, (pct / 100) * w)
  doc.setFillColor(...color)
  doc.roundedRect(x, y, fillW, 4, 1, 1, 'F')
  // Label
  if (label) {
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.gray)
    doc.text(label, x, y - 2)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.navy)
    doc.text(`${pct}%`, x + w, y - 2, { align: 'right' })
  }
}

// ══════════════════════════════════════════════════════════
//  REPORTE SEMANAL
// ══════════════════════════════════════════════════════════
export async function generarReporteSemanal (datos) {
  await cargarJsPDF()
  if (!jsPDF) { alert('Error cargando generador PDF. Verifica tu conexión.'); return }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const semana = datos.semana || Math.ceil(new Date().getDate() / 7)
  const proyecto = datos.proyecto?.nombre || 'Proyecto'

  let y = addHeader(doc, `Reporte Semanal`, `Semana ${semana} — ${new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}`, proyecto)

  // SECCIÓN: RESUMEN EJECUTIVO
  y += 4
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text('RESUMEN EJECUTIVO DE AVANCE', 14, y)
  y += 6

  // KPIs
  const proj = datos.proyecto || {}
  addKpiCard(doc, 14, y, 40, 24, `${proj.avanceFisico || 0}%`, 'AVANCE FÍSICO', COLORS.blue)
  addKpiCard(doc, 58, y, 40, 24, `${proj.avanceFinanciero || 0}%`, 'AVANCE FINANCIERO', COLORS.orange)
  addKpiCard(doc, 102, y, 40, 24, `${proj.diasTranscurridos || 0}`, 'DÍAS TRANSCURRIDOS', COLORS.amber)
  addKpiCard(doc, 146, y, 50, 24, `${datos.incidencias?.length || 0} activas`, 'INCIDENCIAS', COLORS.red)
  y += 32

  // Barras de progreso
  addProgressBar(doc, 14, y, 182, proj.avanceFisico || 0, COLORS.blue, 'Avance físico')
  y += 12
  addProgressBar(doc, 14, y, 182, proj.avanceFinanciero || 0, COLORS.orange, 'Avance financiero')
  y += 12
  addProgressBar(doc, 14, y, 182, Math.round(((proj.diasTranscurridos || 0) / (proj.diasContractuales || 200)) * 100), COLORS.amber, 'Tiempo transcurrido')
  y += 16

  // Línea separadora
  doc.setFillColor(...COLORS.orange)
  doc.rect(14, y, 182, 0.5, 'F')
  y += 6

  // SECCIÓN: CRONOGRAMA DE FASES
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text('ESTADO DE FASES — CRONOGRAMA', 14, y)
  y += 4

  const fasesData = (datos.fases || []).map(f => [
    f.nombre,
    f.responsable || '—',
    f.fechaInicio || '—',
    f.fechaFin || '—',
    {
      content: `${f.pct || 0}%`,
      styles: { textColor: f.pct >= 100 ? COLORS.green : f.status === 'demora' ? COLORS.amber : COLORS.blue, fontStyle: 'bold' }
    },
    {
      content: f.status === 'completado' ? '✓ Completado' : f.status === 'demora' ? '⚠ Con demora' : f.status === 'progreso' ? 'En progreso' : 'Pendiente',
      styles: {
        fillColor: f.status === 'completado' ? [234, 243, 222] : f.status === 'demora' ? [250, 238, 218] : f.status === 'progreso' ? [230, 241, 251] : [241, 239, 232],
        textColor: f.status === 'completado' ? [39, 80, 10] : f.status === 'demora' ? [99, 56, 6] : f.status === 'progreso' ? [12, 68, 124] : [68, 68, 65]
      }
    }
  ])

  doc.autoTable({
    startY: y,
    head: [['Fase / Actividad', 'Responsable', 'Inicio', 'Fin planeado', '%', 'Estado']],
    body: fasesData,
    theme: 'grid',
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 60 }, 4: { halign: 'center', cellWidth: 15 }, 5: { cellWidth: 32 } },
    margin: { left: 14, right: 14 }
  })
  y = doc.lastAutoTable.finalY + 8

  // SECCIÓN: INCIDENCIAS
  if (datos.incidencias?.length > 0) {
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.navy)
    doc.text('INCIDENCIAS ACTIVAS', 14, y)
    y += 4

    const incData = datos.incidencias.map(i => [
      i.titulo,
      i.severidad?.toUpperCase() || '—',
      i.zona || '—',
      i.responsable || 'Sin asignar',
      new Date(i.createdAt).toLocaleDateString('es-MX'),
      i.status
    ])

    doc.autoTable({
      startY: y,
      head: [['Incidencia', 'Severidad', 'Zona', 'Responsable', 'Fecha', 'Estado']],
      body: incData,
      theme: 'grid',
      headStyles: { fillColor: COLORS.red, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 1: { halign: 'center', cellWidth: 20 } },
      margin: { left: 14, right: 14 }
    })
    y = doc.lastAutoTable.finalY + 8
  }

  // SECCIÓN: NOTAS DE BITÁCORA
  if (datos.bitacora?.length > 0) {
    if (y > 220) { doc.addPage(); y = 20 }
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...COLORS.navy)
    doc.text('REGISTROS DE BITÁCORA — SEMANA', 14, y)
    y += 4

    const bitaData = datos.bitacora.slice(0, 10).map(b => [
      b.autorNombre || '—',
      b.zona || '—',
      b.categoria || '—',
      b.texto?.substring(0, 80) + (b.texto?.length > 80 ? '...' : '') || '—',
      new Date(b.createdAt).toLocaleDateString('es-MX')
    ])

    doc.autoTable({
      startY: y,
      head: [['Supervisor', 'Zona', 'Categoría', 'Descripción', 'Fecha']],
      body: bitaData,
      theme: 'grid',
      headStyles: { fillColor: COLORS.blue, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7, cellPadding: 3 },
      columnStyles: { 3: { cellWidth: 70 } },
      margin: { left: 14, right: 14 }
    })
  }

  // FOOTER todas las páginas
  const totalPages = doc.internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    addFooter(doc, i, totalPages)
  }

  const filename = `CONSULTEC_Reporte_Semanal_Sem${semana}_${new Date().getFullYear()}.pdf`
  doc.save(filename)
  console.log(`✅ PDF generado: ${filename}`)
  return filename
}

// ══════════════════════════════════════════════════════════
//  REPORTE DE INCIDENCIA
// ══════════════════════════════════════════════════════════
export async function generarReporteIncidencia (incidencia, proyecto) {
  await cargarJsPDF()
  if (!jsPDF) return

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  let y = addHeader(doc, 'Reporte de Incidencia', incidencia.titulo, proyecto?.nombre)

  y += 4
  // Datos de la incidencia
  const severidadColor = incidencia.severidad === 'alta' ? COLORS.red : incidencia.severidad === 'media' ? COLORS.amber : COLORS.green

  doc.setFillColor(...severidadColor)
  doc.roundedRect(14, y, 182, 8, 2, 2, 'F')
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.white)
  doc.text(`SEVERIDAD: ${incidencia.severidad?.toUpperCase() || 'NO DEFINIDA'} — ${incidencia.titulo}`, 105, y + 5.5, { align: 'center' })
  y += 14

  doc.autoTable({
    startY: y,
    body: [
      ['Número de incidencia', incidencia.id || '—', 'Fecha de registro', new Date(incidencia.createdAt).toLocaleString('es-MX')],
      ['Zona afectada', incidencia.zona || '—', 'Estado actual', incidencia.status || '—'],
      ['Registrado por', incidencia.autorNombre || '—', 'Responsable asignado', incidencia.responsable || 'Sin asignar'],
      ['Proyecto', proyecto?.nombre || '—', 'Evidencias fotográficas', `${incidencia.fotos?.length || 0} fotos adjuntas`]
    ],
    theme: 'grid',
    bodyStyles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { fontStyle: 'bold', fillColor: COLORS.light, textColor: COLORS.navy }, 2: { fontStyle: 'bold', fillColor: COLORS.light, textColor: COLORS.navy } },
    margin: { left: 14, right: 14 }
  })
  y = doc.lastAutoTable.finalY + 8

  // Descripción
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text('DESCRIPCIÓN DETALLADA', 14, y)
  y += 5

  doc.setFillColor(...COLORS.light)
  doc.rect(14, y, 182, 24, 'F')
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...COLORS.navy)
  const textLines = doc.splitTextToSize(incidencia.descripcion || 'Sin descripción', 175)
  doc.text(textLines, 18, y + 6)
  y += 30

  // Espacio para firma
  y += 8
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text('FIRMAS DE VALIDACIÓN', 14, y)
  y += 8

  // Cuadros de firma
  const firmantes = ['Supervisor de obra', 'Residente / Jefe de área', 'Director / Gerente']
  firmantes.forEach((f, i) => {
    const x = 14 + (i * 62)
    doc.setDrawColor(...COLORS.border || [200, 210, 230])
    doc.rect(x, y, 56, 24)
    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...COLORS.gray)
    doc.text(f, x + 28, y + 22, { align: 'center' })
  })

  addFooter(doc, 1, 1)

  const filename = `CONSULTEC_INC_${incidencia.id}_${new Date().toLocaleDateString('es-MX').replace(/\//g, '-')}.pdf`
  doc.save(filename)
  return filename
}

// ══════════════════════════════════════════════════════════
//  REPORTE EJECUTIVO (para Director/Gerente)
// ══════════════════════════════════════════════════════════
export async function generarInformeEjecutivo (proyectos) {
  await cargarJsPDF()
  if (!jsPDF) return

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  let y = addHeader(doc, 'Informe Ejecutivo', `Estado general de proyectos — ${new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}`, null)

  y += 4
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...COLORS.navy)
  doc.text('RESUMEN EJECUTIVO — TODOS LOS PROYECTOS', 14, y)
  y += 6

  const proyData = (proyectos || []).map(p => [
    p.nombre,
    p.tipo?.replace(/_/g, ' ') || '—',
    `${p.avanceFisico || 0}%`,
    `${p.avanceFinanciero || 0}%`,
    `$${((p.presupuestoEjercido || 0) / 1000000).toFixed(1)}M`,
    `$${((p.presupuesto || 0) / 1000000).toFixed(1)}M`,
    p.incidenciasAbiertas || 0,
    p.status === 'activo' ? 'En ejecución' : p.status
  ])

  doc.autoTable({
    startY: y,
    head: [['Proyecto', 'Tipo', 'Físico', 'Financiero', 'Ejercido', 'Total', 'Incid.', 'Estado']],
    body: proyData,
    theme: 'grid',
    headStyles: { fillColor: COLORS.navy, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, cellPadding: 3 },
    columnStyles: { 0: { cellWidth: 50 } },
    margin: { left: 14, right: 14 }
  })

  addFooter(doc, 1, 1)

  const filename = `CONSULTEC_Informe_Ejecutivo_${new Date().getFullYear()}_${(new Date().getMonth() + 1).toString().padStart(2,'0')}.pdf`
  doc.save(filename)
  return filename
}
